import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { Box, Button, Card, CardContent, IconButton, Input, InputAdornment, Tooltip, Dialog, DialogActions, DialogTitle, FormGroup, FormControlLabel, Switch, Grid, Typography, Chip, Avatar, ToggleButton, ToggleButtonGroup, Divider, Stack, Paper, Zoom, Pagination, FormControl, Select, MenuItem, InputLabel } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { contestServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EditOutlined } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import { capitalizeFirstLetter, TrimText } from "../../utils/helpers";
import { fDate, fDate1 } from "../../utils/formatTime";
import { formatPrice } from "../../utils/formatNumber";
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

export default function ContestManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalActiveContests, setTotalActiveContests] = useState(0);
  const [totalInactiveContests, setTotalInactiveContests] = useState(0);

  const fetchBidPackage = async (query, page = currentPage) => {
    const body = {
      searchItem: query || "",
      pageNumber: page,
      pageSize: pageSize,
      status: status,
    };

    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await contestServices.getContest(body);
      if (res.status === 200) {
        setTimeout(() => {
          setData(res?.data?.data?.contests);
          setTotalPages(res?.data?.data?.totalRecords);

          if (res?.data?.data?.totalActiveContests !== undefined) {
            setTotalActiveContests(res.data.data.totalActiveContests);
          }
          if (res?.data?.data?.totalInactiveContests !== undefined) {
            setTotalInactiveContests(res.data.data.totalInactiveContests);
          }

          setLoaderDelay(true);
        }, 500);
      }
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(true);
    } finally {
      setIsLoading(false)
    }
  };

  const fetchTotalStats = async () => {
    try {
      const res = await contestServices.getContest({
        searchItem: "",
        pageNumber: 1,
        pageSize: 1000,
        status: [],
      });

      if (res.status === 200 && res?.data?.data?.contests) {
        const allContests = res.data.data.contests;
        setTotalActiveContests(allContests.filter(item => item.status === 1).length);
        setTotalInactiveContests(allContests.filter(item => item.status === 0).length);
      }
    } catch (err) { }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchBidPackage(query, currentPage), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
    // Fetch data for the new page
    fetchBidPackage(searchItem, page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this contest?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await contestServices.deleteContest({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await contestServices.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  // Initialize currentPage and viewMode from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
  }, []); // Only run once on mount

  useEffect(() => {
    fetchBidPackage(searchItem);
    // Fetch total statistics on component mount
    fetchTotalStats();
  }, [currentPage, pageSize, status, refresh]);

  // Separate effect for view mode changes to avoid unnecessary data fetching
  useEffect(() => {
    // Only update URL when view mode changes, don't fetch data
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('view', viewMode);
    setSearchParams(newSearchParams);
  }, [viewMode]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getGameTypeIcon = (gameType) => {
    switch (gameType?.toLowerCase()) {
      case 'football':
        return <SportsSoccerIcon />;
      case 'basketball':
        return <SportsSoccerIcon />;
      default:
        return <EmojiEventsIcon />;
    }
  };

  const calculateStats = () => {
    const totalContests = totalPages;

    const activeContests = totalActiveContests > 0 ? totalActiveContests : data.filter(item => item.status === 1).length;
    const inactiveContests = totalInactiveContests > 0 ? totalInactiveContests : data.filter(item => item.status === 0).length;

    return { totalContests, activeContests, inactiveContests };
  };

  const renderContestCard = (contest, index) => (
    <Zoom in={!isLoading} className={`contest-card-zoom contest-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={contest._id}>
        <Card className="contest-card">
          <Box className="contest-card-index-badge">
            {(currentPage - 1) * pageSize + index + 1}
          </Box>

          <CardContent className="contest-card-content">
            <Box className="contest-card-header">
              <Avatar className="contest-card-avatar">
                {getGameTypeIcon(contest.gameType)}
              </Avatar>
              <Box className="contest-card-title-container">
                <Typography variant="h5" className="contest-card-title">
                  {TrimText(contest.contestName, 25)}
                </Typography>
                <Chip
                  label={capitalizeFirstLetter(contest?.gameType || 'Unknown')}
                  size="small"
                  className="contest-card-type-chip"
                />
              </Box>
            </Box>

            <Divider className="contest-card-divider" />

            <Grid container spacing={2} className="contest-card-details-grid">
              <Grid item xs={6}>
                <Stack spacing={2}>

                  <Box className="contest-card-detail-item">
                    <Typography variant="caption" className="contest-card-detail-label">
                      League Type
                    </Typography>
                    <Typography variant="body2" className="contest-card-detail-value">
                      {contest.leagueId?.name ? capitalizeFirstLetter(contest.leagueId?.name) : 'N/A'}
                    </Typography>
                  </Box>

                  {contest.contestCommission && (
                    <Box className="contest-card-detail-item">
                      <Typography variant="caption" className="contest-card-detail-label">
                        Commission
                      </Typography>
                      <Typography variant="body2" className="contest-card-detail-value-commission">
                        {contest.contestCommission}%
                      </Typography>
                    </Box>
                  )}

                  <Box className="contest-card-detail-item">
                    <Typography variant="caption" className="contest-card-detail-label">
                      Revenue
                    </Typography>
                    <Typography variant="body2" className="contest-card-detail-value">
                      ${formatPrice(contest?.totalRevenue || contest?.revenue || 0)}
                    </Typography>
                  </Box>

                  <Box className="contest-card-detail-item">
                    <Typography variant="caption" className="contest-card-detail-label">
                      Contest Type
                    </Typography>
                    <Typography variant="body2" className="contest-card-detail-value">
                      {contest.contestType?.type ? capitalizeFirstLetter(contest.contestType.type) : 'N/A'}
                    </Typography>
                    {contest.contestType?.playerPosition && (
                      <Typography variant="caption" className="contest-card-detail-value-secondary">
                        Position: {contest.contestType.playerPosition === "27" ? "Forward" :
                          contest.contestType.playerPosition === "26" ? "Midfielder" :
                            contest.contestType.playerPosition === "25" ? "Defender" :
                              contest.contestType.playerPosition === "24" ? "Goalkeeper" : "N/A"}
                      </Typography>
                    )}
                    {contest.contestType?.actionType && (
                      <Typography variant="caption" className="contest-card-detail-value-secondary">
                        Action: {capitalizeFirstLetter(contest.contestType.actionType)}
                      </Typography>
                    )}
                    {contest.contestType?.negativeType && (
                      <Typography variant="caption" className="contest-card-detail-value-secondary">
                        Negative: {capitalizeFirstLetter(contest.contestType.negativeType)}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={6}>
                <Stack spacing={2}>

                  <Box className="contest-card-detail-item">
                    <Typography variant="caption" className="contest-card-detail-label">
                      Commission Amount
                    </Typography>
                    <Typography variant="body2" className="contest-card-detail-value">
                      ${formatPrice(contest?.totalCommission || contest?.commission || 0)}
                    </Typography>
                  </Box>

                  <Box className="contest-card-detail-item">
                    <Typography variant="caption" className="contest-card-detail-label">
                      Created {fDate(contest.createdAt)}
                    </Typography>
                    <Typography variant="body2" className="contest-card-detail-value">
                      by {capitalizeFirstLetter(contest.createdByRole === "users" ? "User" : "Admin")}
                    </Typography>
                  </Box>
                  
                  <Box className="contest-card-detail-item">
                    <Typography variant="caption" className="contest-card-detail-label">
                      Contest Status
                    </Typography>
                    <Chip
                      label={capitalizeFirstLetter(contest?.contestStatus || 'Unknown')}
                      size="small"
                      color={getStatusColor(contest.contestStatus)}
                      className="contest-card-status-chip"
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            {contest.gameDuration && (
              <>
                <Divider className="contest-card-divider" />
                <Box className="contest-card-duration-section">
                  <Typography variant="caption" className="contest-card-duration-title">
                    Duration
                  </Typography>
                  <Typography variant="body2" className="contest-card-duration-value">
                    {contest.gameDuration?.type ? capitalizeFirstLetter(contest.gameDuration.type) : 'N/A'}
                  </Typography>
                  {contest.gameDuration?.startTime && contest.gameDuration?.endTime && (
                    <Box className="contest-card-duration-time-container">
                      <Box className="contest-card-time-item">
                        <Typography variant="caption" className="contest-card-time-label-start">
                          Start:
                        </Typography>
                        <Typography variant="caption" className="contest-card-time-value">
                          {fDate1(contest.gameDuration.startTime)}
                        </Typography>
                      </Box>
                      <Box className="contest-card-time-item">
                        <Typography variant="caption" className="contest-card-time-label-end">
                          End:
                        </Typography>
                        <Typography variant="caption" className="contest-card-time-value">
                          {fDate1(contest.gameDuration.endTime)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </>
            )}

            <Divider className="contest-card-divider" />

            <Box className="contest-card-actions">
              <Tooltip title="Fantasy Games">
                <IconButton
                  size="small"
                  className="contest-action-button"
                  onClick={() => navigate(`/contest-management/fantasy-games/${contest?._id}`)}
                >
                  <SportsSoccerIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Leaderboard">
                <IconButton
                  size="small"
                  className="contest-action-button-leaderboard"
                  onClick={() => navigate(`/contest-management/leaderboard/${contest?._id}`)}
                >
                  <LeaderboardIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="View">
                <IconButton
                  size="small"
                  className="contest-action-button-view"
                  onClick={() => navigate(`/contest-management/view/${contest?._id}`)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  className="contest-action-button-edit"
                  onClick={() => navigate(`/contest-management/edit/${contest?._id}?page=${currentPage}&view=${viewMode}`)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  className="contest-action-button-delete"
                  onClick={() => deleteData(contest._id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box className="contest-card-status-container">
              <Typography variant="caption" className="contest-card-status-text">
                {contest.status === 1 ? '🟢 Active' : '🔴 Inactive'}
              </Typography>
              <Switch
                checked={contest.status === 1}
                onChange={() => {
                  setShow(true);
                  setCurrentStatus(contest.status);
                  setStatusId(contest._id);
                  setWarning(
                    `Are you sure you want to ${contest.status === 1 ? "deactivate" : "activate"} this contest?`
                  );
                }}
                className="contest-card-status-switch"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Zoom>
  );

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "75px",
    },
    {
      name: "Contest Name",
      sortable: true,
      selector: (row) => TrimText(row.contestName, 20),
      width: "160px",
    },
    {
      name: "Game Type",
      sortable: true,
      selector: (row) => {
        const contestType = row.contestType || {};
        const type = capitalizeFirstLetter(contestType.type || '');

        const positionMap = {
          "27": "Forward",
          "26": "Midfielder",
          "25": "Defender",
          "24": "Goalkeeper",
        };

        const playerRole = positionMap[contestType.playerPosition] || '';
        const actionType = contestType.actionType
          ? capitalizeFirstLetter(contestType.actionType)
          : '';
        const negativeType = contestType.negativeType
          ? capitalizeFirstLetter(contestType.negativeType)
          : '';

        return (
          <div>
            <div>{type}</div>

            {(contestType.type === 'action' || contestType.type === 'position') && playerRole && (
              <div><strong>Player Position:</strong> {playerRole}</div>
            )}

            {contestType.type === 'action' && actionType && (
              <div><strong>Action:</strong> {actionType}</div>
            )}

            {contestType.type === 'negative-point' && negativeType && (
              <div><strong>Negative Type:</strong> {negativeType}</div>
            )}
          </div>
        );
      },
      width: "240px",
    },
    {
      name: "Contest Type",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(row?.gameType),
      width: "160px",
    },
    {
      name: "Commission %",
      selector: (row) => row?.contestCommission ? row?.contestCommission + "%" : 'N/A',
      width: "160px",
    },
    {
      name: "Revenue",
      sortable: true,
      selector: (row) => {
        const revenue = row?.totalRevenue || row?.revenue || 0;
        return `$${formatPrice(revenue)}`;
      },
      width: "120px",
    },
    {
      name: "Commission Amount",
      sortable: true,
      selector: (row) => {
        const commission = row?.totalCommission || row?.commission || 0;
        return `$${formatPrice(commission)}`;
      },
      width: "140px",
    },
    {
      name: "Game Duration",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(row.gameDuration?.type),
      width: "160px",
    },
    {
      name: "Game Duration",
      sortable: true,
      selector: (row) => {
        const start = fDate1(row.gameDuration?.startTime);
        const end = fDate1(row.gameDuration?.endTime);

        return (
          <div>
            <div><strong>Start:</strong> {start}</div>
            <div><strong>End:</strong> {end}</div>
          </div>
        );
      },
      width: "200px",
    },
    {
      name: "Contest Status",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(row?.contestStatus),
      width: "160px",
    },
    {
      name: "Created By",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(row.createdByRole === "Users" ? "User" : "Admin"),
      width: "160px",
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
      width: "160px",
    },
    {
      name: "Status",
      width: "80px",
      cell: (row) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={row.status === 1}
                onClick={() => {
                  setShow(true);
                  setCurrentStatus(row.status);
                  setStatusId(row._id);
                  setWarning(
                    `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"} this contest?`
                  );
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </FormGroup>
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="Fantasy Games" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/contest-management/fantasy-games/${row?._id}`);
              }}
            >
              <SportsSoccerIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Leaderboard" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/contest-management/leaderboard/${row?._id}`);
              }}
            >
              <LeaderboardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/contest-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/contest-management/edit/${row?._id}?page=${currentPage}&view=${viewMode}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    }
  ];

  const stats = calculateStats();

  return (
    <Box className="contest-management-container">
      {/* Header Section */}
      <Paper
        elevation={0}
        className="contest-header-paper"
      >
        <Box className="contest-header-content">
          <Box className="contest-header-left">
            <Avatar className="contest-header-avatar">
              <EmojiEventsIcon className="contest-header-icon" />
            </Avatar>
            <Box>
              <Typography variant="h4" className="contest-header-title">
                Contest Management
              </Typography>
              <Typography variant="body1" className="contest-header-subtitle">
                Manage and monitor all contests in your system
              </Typography>
            </Box>
          </Box>

          <Button
            size="large"
            variant="contained"
            className="contest-add-button"
            onClick={() => navigate("/contest-management/add")}
          >
            + Add Contest
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} className="contest-stats-grid">
          <Grid item xs={12} sm={6} md={3}>
            <Paper className="contest-stat-card">
              <Typography variant="h4" className="contest-stat-number">
                {stats.totalContests}
              </Typography>
              <Typography variant="body2" className="contest-stat-label">
                Total Contests
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className="contest-stat-card-active">
              <Typography variant="h4" className="contest-stat-number-active">
                {stats.activeContests}
              </Typography>
              <Typography variant="body2" className="contest-stat-label">
                Active Contests
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper className="contest-stat-card-inactive">
              <Typography variant="h4" className="contest-stat-number-inactive">
                {stats.inactiveContests}
              </Typography>
              <Typography variant="body2" className="contest-stat-label">
                Inactive Contests
              </Typography>
            </Paper>
          </Grid>
        </Grid>

      </Paper>

      {/* Controls Section */}
      <Paper
        elevation={0}
        className="contest-controls-paper"
      >
        <Box className="contest-controls-content">
          <Box className="contest-controls-left">
            <TableFilter
              data={STATUS_OPTIONS_LIST}
              value={status}
              setValue={setStatus}
              filterName="Status"
            />

            <Input
              value={searchItem}
              placeholder="Search contests..."
              onChange={handleSearchChange}
              className="contest-search-input"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear-search"
                    onClick={() => {
                      const cleared = "";
                      setSearchItem(cleared);
                      debouncedSearch(cleared);
                    }}
                    className="contest-clear-search-button"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode) {
                setViewMode(newMode);
                // URL will be updated by useEffect
              }
            }}
            className="contest-view-toggle"
          >
            <ToggleButton value="grid">
              <ViewModuleIcon />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Content Section */}
      <Paper
        elevation={0}
        className="contest-content-paper"
      >
        {viewMode === 'grid' ? (
          /* Grid View */
          <Box className="contest-grid-container">
            {isLoading || !loaderDelay ? (
              <Box className="contest-loading-container">
                <Loader />
              </Box>
            ) : data.length === 0 ? (
              <Box className="contest-empty-container">
                <Typography variant="h6" className="contest-empty-title">
                  No contests found
                </Typography>
                <Typography variant="body2" className="contest-empty-subtitle">
                  Try adjusting your search criteria or create a new contest
                </Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={3} className="contest-grid">
                  {data.map((contest, index) => renderContestCard(contest, index))}
                </Grid>

                {/* Pagination for Grid View */}
                <Box className="contest-pagination-container">
                  <Box className="contest-pagination-info">
                    <Typography variant="body2" className="contest-pagination-text">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPages)} of {totalPages} contests
                    </Typography>
                  </Box>

                  <Box className="contest-pagination-controls">
                    <FormControl size="small" className="contest-page-size-select">
                      <InputLabel>Page Size</InputLabel>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e.target.value)}
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>

                    <Pagination
                      count={Math.ceil(totalPages / pageSize)}
                      page={currentPage}
                      onChange={(e, page) => handlePageChange(page)}
                      color="primary"
                      className="contest-pagination"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        ) : (
          /* List View */
          <Box>
            <DataTable
              columns={columns}
              data={data}
              pagination
              paginationServer
              paginationTotalRows={totalPages}
              paginationCurrentPage={currentPage}
              paginationDefaultPage={currentPage}
              onChangePage={handlePageChange}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 20, 50]}
              onChangeRowsPerPage={(currentRowsPerPage) => handlePageSizeChange(currentRowsPerPage)}
              progressPending={isLoading || !loaderDelay}
              progressComponent={<Loader />}
              className="contest-datatable"
            />
          </Box>
        )}
      </Paper>

      {/* Status Change Dialog */}
      <Dialog open={show} onClose={() => setShow(false)} className="contest-status-dialog">
        <DialogTitle className="contest-status-dialog-title">{warning}</DialogTitle>
        <DialogActions className="contest-status-dialog-actions">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button variant="outlined" onClick={() => setShow(false)} color="primary">
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}